/**
 * Central marketing messaging for MyOrgLab: enterprise AI & analytics builders.
 *
 * Single source of truth for hero copy, solution taxonomy, pillars, quotes,
 * and reusable section copy. See `.cursor/rules/marketing-positioning.mdc`.
 */

import { PARTNER_LOGOS } from './staticAssets';

export const MESSAGING_EYEBROW = 'Enterprise AI · Platform builders';

/** Powered by Snowflake brand placement: subtle, professional sizing. */
export const POWERED_BY_SNOWFLAKE = {
  src: PARTNER_LOGOS.poweredBySnowflake,
  alt: 'Powered by Snowflake',
};

/** Company-level narrative: integrated home hero and about positioning. */
export const COMPANY_HERO = {
  eyebrow: MESSAGING_EYEBROW,
  title: 'Governed AI and decision agents built in your cloud account.',
  tagline:
    'Domain analyst agents on semantic views, a master orchestrator for real work, and MCP tool catalogs so operators can ask and apply approved writes under your identity, grounded in Snowflake-centered data and meaning you own. That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow. ' +
    'Collapse fragmented transactional systems, analytical warehouses, and document repositories into one intelligence platform your team operates.',
  description:
    'MyOrgLab is a team of platform builders, product partners, and implementation specialists, not a multi-tenant SaaS vendor. ' +
    'We deploy governed analytics products like Semantic HR in your Snowflake account and compose specialist agents, orchestration, and Cortex Intelligence ' +
    'on semantic foundations, modern BI migrations, and searchable dashboard marketplaces, on your cloud and Microsoft stack. That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
  primaryCta: { label: 'Explore Semantic HR', href: '/semantic-hr' },
  secondaryCta: { label: 'Get a demo', href: '/contact' },
};

/**
 * Production home hero copy (myorglab.com): rendered by HeroPrimary.jsx.
 * Four strategic cards: migrations & AIM, semantic intelligence, interactive analytics, AI agents.
 */
export const HERO_PRODUCTION = {
  eyebrow: 'Enterprise AI · Platform builders · Snowflake-native agents',
  titleLead: 'Governed AI and decision agents',
  titleHighlight: ' built in your account.',
  subtitle:
    'MyOrgLab builds governed AI and decision-agent systems in your cloud: domain analysts on semantic views, orchestrated for real work, grounded in Snowflake-centered data and meaning you own. That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
  primaryCta: { label: 'See What We Build', href: '/platform-engineering' },
  secondaryCta: { label: 'Book a Free Discovery Call', href: '/contact' },
  partnersEyebrow: 'Partners & Ecosystem',
  seoTitle: 'MyOrgLab | Governed AI and decision agents',
  seoDescription:
    'MyOrgLab builds governed AI and decision-agent systems in your cloud account: domain analysts on semantic views, a master orchestrator, and products like Semantic HR that you operate.',
};

/** @deprecated Use HERO_PRODUCTION (kept for imports that expect HERO_PRIMARY). */
export const HERO_PRIMARY = HERO_PRODUCTION;

/** Three production hero service cards: label inside card, equal-height grid. */
export const HERO_CARDS = [
  {
    label: 'Snowflake & Data Platform Modernization',
    title: 'Modernized Migrations & AIM',
    body:
      'Snowflake-centered enterprise AI with migration agents (AIM), Teradata, SQL Server, legacy BI, and ' +
      'warehouses to Snowflake on AWS and Azure with dbt, build on platforms you trust and accelerate migration programs.',
    href: '/platform-engineering#snowflake',
  },
  {
    label: 'Semantic Intelligence',
    title: 'Semantic Foundation for Enterprise AI',
    body:
      'Build the semantic layer, domain memory for agents, and certified metric logic, the contract specialist ' +
      'analyst agents specialize on, with Snowflake-native builders and implementation partners. ' +
      'That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
    href: '/platform-engineering#modernization',
  },
  {
    label: 'Interactive & Conversational Analytics',
    title: 'Interactive Analytics & Decision Intelligence',
    body:
      'Legacy BI to a searchable marketplace plus Streamlit hubs and talk-to-data on Snowflake, embedded Power BI, Tableau, and QuickSight, ' +
      'conversational analytics that complements dashboards on the same semantic foundation.',
    href: '/interactive-analytics',
  },
  {
    label: 'AI & Decision Agents',
    title: 'Domain agents, orchestrated in your account',
    body:
      'Snowflake-native agent systems: domain analyst agents on governed semantic views, a master orchestrator for multi-step work, ' +
      'and an MCP tool catalog so operators can ask, and apply approved writes, in chat, under your identity and policy. ' +
      'That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
    href: '/platform-engineering#ai',
  },
];

/**
 * Supporting "Enterprise Intelligence Ecosystem" section: repositioned below the
 * short primary hero. Carries the detailed platform/service narrative + flagship proof.
 */
export const ENTERPRISE_ECOSYSTEM = {
  eyebrow: 'Platform modernization',
  title: 'Governed AI in your account, on the platforms you already trust.',
  body:
    'MyOrgLab is a team of platform builders, product partners, and implementation specialists, not a multi-tenant SaaS vendor. ' +
    'We ship governed analytics products in your Snowflake account and compose specialist domain agents, a master orchestrator, ' +
    'and optional MCP interoperability under your identity, on Snowflake-centered semantic foundations, modern BI migrations, ' +
    'and searchable dashboard marketplaces your team owns. That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
};

/** Solution pills across the home hero: maps to strategic categories. */
export const HERO_SOLUTION_PILLS = [
  { label: 'Modernized Migrations', href: '/platform-engineering#snowflake', accent: 'migration' },
  { label: 'Semantic Intelligence', href: '/platform-engineering#modernization', accent: 'semantic' },
  { label: 'Interactive Analytics', href: '/interactive-analytics', accent: 'brand' },
  { label: 'Conversational BI', href: '/semantic-hr', accent: 'semantic' },
  { label: 'AI Agents', href: '/platform-engineering#ai', accent: 'enterprise' },
  { label: 'Power BI Operations', href: '/interactive-analytics', accent: 'brand' },
  { label: 'Data Platform Engineering', href: '/platform-engineering', accent: 'migration' },
];

/** Ecosystem platforms: hero badges and broad site references (not migration-only stacks). */
export const PLATFORM_BADGES = [
  'Snowflake',
  'AWS',
  'Azure',
  'Power BI',
  'Tableau',
  'Streamlit',
  'QuickSight',
  'Postgres',
  'Cortex Search',
];

/** Migration & modernization platforms: nav migrations category, MIGRATION_SCOPE, migration pages only. */
export const MIGRATION_PLATFORMS = [
  'Snowflake',
  'Teradata',
  'SQL Server',
  'dbt',
  'Terraform',
  'Legacy BI',
  'Power BI',
  'Tableau',
  'Amazon QuickSight',
  'AWS',
  'Azure',
];

/**
 * Strategic nav solution categories.
 * `description` holds the original full-length verbiage for page bodies.
 * `shortDescription` is the nav dropdown body (2-line clamp fallback).
 */
export const NAV_SOLUTIONS = [
  {
    id: 'migrations-aim',
    name: 'Modernized Migrations',
    description:
      'AI-assisted migration (AIM) and Snowflake migration agents, Teradata and SQL Server uplift, legacy BI, Tableau, and Amazon QuickSight ' +
      'modernization, dbt analytics engineering, and governed pipelines on AWS and Azure, on the foundation you already trust.',
    shortDescription:
      'Snowflake, SQL Server, Teradata, dbt, AWS, and Azure migration strategy.',
    href: '/platform-engineering#snowflake',
    icon: 'CloudIcon',
  },
  {
    id: 'conversational-semantic',
    name: 'Semantic Intelligence',
    description:
      'Enterprise semantic layers, certified metrics, and domain memory, the semantic contract specialist analyst agents ' +
      'and copilots inherit for talk-to-data and conversational analytics, not guesses.',
    shortDescription:
      'Enterprise semantic layers, metric logic, and domain memory for AI.',
    href: '/platform-engineering#modernization',
    icon: 'CircleStackIcon',
  },
  {
    id: 'operational-bi',
    name: 'Interactive Analytics',
    description:
      'Embedded Power BI, Tableau, and Amazon QuickSight design and migration, conversational BI, searchable dashboard marketplaces, BI catalog and ' +
      'dashboard intelligence, executive reporting portals, Cortex Search for document and unstructured knowledge retrieval, and operational intelligence, interactive analytics with metric monitoring, flags, triggers, and alerts on Snowflake.',
    shortDescription:
      'Power BI, conversational BI, searchable dashboards, and operational intelligence.',
    href: '/interactive-analytics',
    icon: 'ChartBarSquareIcon',
  },
  {
    id: 'ai-analytics',
    name: 'AI & Decision Agents',
    description:
      'Snowflake-native agent systems in your account: domain analyst agents on governed semantic views, a master orchestrator for multi-step work, and an MCP tool catalog so operators can ask and apply approved warehouse updates in chat, and so third-party agents can use that expertise under your identity and policy, plus Cortex, predictive intelligence, and closed-loop monitoring with human oversight.',
    shortDescription:
      'Domain agents, governed MCP, and chat-operated warehouse updates in your Snowflake account.',
    href: '/platform-engineering#ai',
    icon: 'CpuChipIcon',
  },
  {
    id: 'platform-engineering',
    name: 'Platform Engineering',
    description:
      'Modern migrations from Teradata, SQL Server, and legacy BI; Snowflake AIM and migration agents; Streamlit application engineering; ' +
      'integrations and data platform modernization on AWS and Azure, one cohesive data-to-decision ecosystem.',
    shortDescription:
      'Data pipelines, Streamlit apps, cloud architecture, and analytics buildout.',
    href: '/platform-engineering',
    icon: 'CubeTransparentIcon',
  },
];

/** Build-vs-buy positioning: builders on client foundations. */
export const BUILD_VS_BUY = {
  headline: 'Platform-native products and implementations on your cloud',
  body:
    'MyOrgLab deploys governed analytics products, like Semantic HR\'s split-screen copilot with embedded BI and ' +
    'on-demand visualizations, in your Snowflake account, alongside semantic layers, BI galleries, and Cortex agents, including ' +
    'composable agent architectures, specialist analysts, orchestration, and governed MCP exposure, not a hosted multi-tenant agent product. ' +
    'Scoped partnerships with operable deliverables, not multi-tenant SaaS subscriptions or open-ended SI retainers. That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
};

/** Flagship product: conversational & semantic pillar. */
export const SEMANTIC_HR_HERO = {
  product: 'Semantic HR',
  tagline: 'Snowflake-Native Workforce Intelligence',
  description:
    'A Snowflake-native workforce intelligence product, governed semantic models for Workday and SuccessFactors, ' +
    'Cortex Intelligence agents, and a split-screen analytics experience, copilot chat with in-thread dynamic ' +
    'visualizations alongside an embedded BI report gallery (Power BI, Tableau, or your platform of choice).',
};

/** Full migration scope: platform-engineering page hero and anchors. */
export const MIGRATION_SCOPE = {
  eyebrow: 'Semantic Platform Engineering',
  title: 'Semantic Platform Engineering for Snowflake Enterprise AI.',
  body:
    'Design Snowflake architecture, semantic model registries, AI-ready data layers, Cortex Search and Cortex Analyst ' +
    'preparation, secure reporting views, and governed consumption layers for Power BI, Tableau, and QuickSight. We align HRIS, CRM, and ERP ' +
    'integrations, data quality controls, and Streamlit or React interfaces into one semantic operating model.',
  platforms: [
    'Snowflake Architecture',
    'Semantic Model Registry',
    'AI-Ready Data Layers',
    'Cortex Search & Cortex Analyst',
    'Secure Reporting Views',
    'HRIS · CRM · ERP Integration',
    'Power BI, Tableau & QuickSight Consumption Layers',
    'Streamlit & React Interfaces',
  ],
  quote:
    'From data pipelines to domain memory, we engineer governed semantic foundations that make enterprise AI practical.',
};

/** Platform engineering page: section copy by anchor. */
export const PLATFORM_ENGINEERING_SECTIONS = {
  snowflake: {
    eyebrow: 'Modern migrations',
    title: 'Legacy warehouse, database, and BI uplift, with AI-assisted migration (AIM).',
    body:
      'Move Teradata, SQL Server, on-prem warehouses, and legacy BI into Snowflake with migration agents, governed ' +
      'pipelines, and Power BI, Tableau, and Amazon QuickSight paths where BI modernization fits. Close the context gap between ' +
      'source systems, metrics, and decisions from day one.',
  },
  modernization: {
    eyebrow: 'Semantic foundation',
    title: 'Enterprise semantic layer design and BI-to-data-layer transformation.',
    body:
      'Modernize legacy BI into searchable, governed dashboard marketplaces. Bridge dashboards, data, and business ' +
      'logic with certified metrics, domain memory for analytics agents, and analytics engineering that moves ' +
      'intelligence from the BI layer to the Snowflake data layer. Combine structured metrics, document context, and ' +
      'Cortex Search indexes so AI agents can answer with both data and enterprise knowledge.',
  },
  ai: {
    eyebrow: 'Snowflake-native agent systems',
    title: 'Domain analyst agents, governed MCP, and chat-operated updates in your account.',
    body:
      'We build Snowflake-native agent systems on a consolidated governed repository: specialist domain analyst agents ' +
      'bound to semantic views, a master orchestrator for multi-step work, and an MCP tool catalog that exposes approved procedures, not raw SQL. ' +
      'Operators work in chat, Snowflake Intelligence, Cortex, or MCP-connected assistants, to ask questions, apply governed writes, ' +
      'and maintain reference facts source systems cannot store. Third-party or peer agents consume the same tools under your identity and policy. ' +
      'Not a generic chatbot over raw tables, and not a multi-tenant agent SaaS product. That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
    architecture: {
      quote:
        'Scalable enterprise intelligence is not one agent that knows everything. It is specialist analysts on governed semantic views, orchestrated in your account, and usable by your agents or others via MCP without breaking trust. The same catalog lets operators apply approved writes in chat, with human oversight on high-stakes actions.',
      steps: [
        { label: 'Consolidated governed repository', detail: 'Models and data products you own' },
        { label: 'Domain semantic views', detail: 'The contract each specialist is bound to' },
        { label: 'Domain analyst agents', detail: 'Finance, operations, workforce, and other SMEs' },
        { label: 'Master orchestrator', detail: 'Routes, delegates, synthesizes, keeps approved context' },
        { label: 'MCP tool catalog', detail: 'Approved read and write procedures, not a security bypass' },
        { label: 'Operator chat', detail: 'Ask, seed, and update governed facts without writing SQL' },
      ],
    },
  },
  hris: {
    eyebrow: 'Workforce & operational intelligence',
    title: 'HRIS and operational command centers on the same semantic foundation.',
    body:
      'HRIS, CRM, ERP, and operational KPI ecosystems, command centers, flags, triggers, and decision ' +
      'intelligence agents that inherit governed domain memory instead of another disconnected report layer.',
  },
};

/** Newsletter section copy: aligned with hero and nav themes. */
export const NEWSLETTER = {
  eyebrow: 'Newsletter',
  title: 'Stay ahead of modern enterprise analytics.',
  subtitle:
    'Get insights on Snowflake-centered AI, modern BI migration, semantic layers, interactive analytics, and ' +
    'decision intelligence systems built on the platforms your enterprise already trusts.',
  pillars: [
    {
      title: 'Migrations & platform engineering',
      body:
        'Snowflake AIM, Teradata and SQL Server uplift, dbt, AWS/Azure strategy, legacy BI modernization, ' +
        'and data ecosystem buildout.',
      accent: 'migration',
    },
    {
      title: 'Interactive & operational BI',
      body:
        'Power BI, Tableau, and QuickSight ops intelligence, conversational BI, searchable dashboard marketplaces, aggregation, ' +
        'flags, triggers, and alerts.',
      accent: 'brand',
    },
    {
      title: 'Semantic layers & AI analytics',
      body:
        'Domain memory, Semantic HR, Cortex copilots, predictive analytics, and measure monitoring on ' +
        'governed data.',
      accent: 'semantic',
    },
  ],
};

/** Home analytics coverage: ContentService (headline, intro, pillars; no bento cards). */
export const ANALYTICS_COVERAGE = {
  eyebrow: 'Analytics coverage',
  title: 'Where your stack converges into one ecosystem.',
  intro:
    'From modernization and platform buildout through semantic layers, Streamlit hubs, interactive and conversational ' +
    'analytics on Power BI and Snowflake, and decision agents, one operating model on the platforms you trust.',
  bullets: [
    'Modernized migrations & AIM',
    'Embedded analytics & dashboard marketplaces',
    'Operational & executive intelligence',
    'AI & decision intelligence',
    'Semantic layers & domain memory',
    'Semantic HR & workforce intelligence',
  ],
  pillars: [
    {
      title: 'Migrations & platform engineering',
      body:
        'Snowflake AIM, Teradata and SQL Server uplift, dbt, AWS/Azure strategy, legacy BI modernization, ' +
        'and data ecosystem buildout.',
    },
    {
      title: 'Interactive & operational BI',
      body:
        'Power BI and Streamlit ops intelligence, talk-to-data conversational analytics, searchable dashboard ' +
        'marketplaces, flags, triggers, and alerts.',
    },
    {
      title: 'Semantic layers & AI analytics',
      body:
        'Domain memory, Semantic HR, Cortex copilots, predictive analytics, and measure monitoring on ' +
        'governed data.',
    },
  ],
};

/** Home analytics coverage links: optional deep links (not rendered as bento tiles). */
export const HOME_SERVICE_SECTIONS = [
  {
    title: 'Modernized migrations & AIM',
    description:
      'Snowflake, Teradata, SQL Server, dbt, AWS, and Azure migration strategy with AI-assisted delivery.',
    href: '/platform-engineering#snowflake',
    accent: 'migration',
  },
  {
    title: 'Embedded analytics & dashboard marketplaces',
    description:
      'Searchable embedded Power BI, interactive reporting portals, and BI aggregation for enterprise operators.',
    href: '/interactive-analytics',
    accent: 'brand',
  },
  {
    title: 'Operational & executive intelligence',
    description:
      'Real-time command centers, KPI ecosystems, flags, triggers, and leadership dashboards.',
    href: '/interactive-analytics',
    accent: 'brand',
  },
  {
    title: 'AI & decision agents',
    description:
      'Domain analyst agents, governed MCP, and chat-operated warehouse updates, plus Cortex and closed-loop monitoring.',
    href: '/platform-engineering#ai',
    accent: 'enterprise',
  },
  {
    title: 'Semantic layers & domain memory',
    description:
      'Certified metrics, analytics engineering, and semantic foundations for enterprise AI.',
    href: '/platform-engineering#modernization',
    accent: 'semantic',
  },
  {
    title: 'Semantic HR & workforce intelligence',
    description:
      'Flagship HR semantic layer with Copilot + Builder on leading HRIS platforms.',
    href: '/semantic-hr',
    accent: 'semantic',
  },
];

/** One source of truth → intelligence block (home Patch section). */
export const ONE_SOURCE_OF_TRUTH = {
  eyebrow: 'Semantic intelligence',
  title: 'From one source of truth to one source of intelligence.',
  titleAccent: 'Governed metrics. Conversational reach.',
  body:
    'Modern enterprises need more than centralized dashboards. They need semantic foundations that connect ' +
    'metrics, business logic, domain knowledge, and operational context. MyOrgLab helps organizations move ' +
    'from traditional BI layers into AI-ready data ecosystems where dashboards are searchable, metrics are ' +
    'monitored, and users can talk directly to trusted enterprise data.',
  primaryCta: { label: 'Book a Free Call', href: '/contact' },
  secondaryCta: { label: 'Explore embedded BI', href: '/interactive-analytics' },
};

/**
 * Five strategic solution categories: primary nav grouping.
 * Dropdown uses NAV_SOLUTIONS `shortDescription` (clamped) plus offering links.
 */
export const SOLUTION_CATEGORIES = NAV_SOLUTIONS.map((nav) => ({
  ...nav,
  description: nav.description,
  offerings: getOfferingsForCategory(nav.id),
}));

function getOfferingsForCategory(categoryId) {
  const map = {
    'migrations-aim': [
      {
        name: 'Snowflake Platforms & Migration (AIM)',
        summary: 'Snowflake-centric platforms, AI migration agent (AIM), Cortex, and governed pipelines.',
        href: '/platform-engineering#snowflake',
      },
    ],
    'operational-bi': [
      {
        name: 'Embedded Power BI & Dashboard Marketplace',
        summary: 'Legacy BI → searchable embedded experiences, executive dashboards, and interactive analytics.',
        href: '/interactive-analytics',
      },
      {
        name: 'Workforce & Operational Intelligence',
        summary: 'Command centers, KPI ecosystems, flags, triggers, and decision-intelligence agents.',
        href: '/platform-engineering#hris',
      },
      {
        name: 'Modernized Migrations (AIM)',
        summary: 'Teradata, SQL Server, legacy BI, and warehouse uplift with AI-assisted Snowflake migration.',
        href: '/platform-engineering#snowflake',
      },
    ],
    'conversational-semantic': [
      {
        // `featured` renders the ™ superscript; keep it out of the name string.
        name: 'Semantic HR',
        summary: 'Out-of-the-box HR semantic layer, Copilot + Builder on Snowflake for leading HRIS platforms.',
        href: '/semantic-hr',
        featured: true,
      },
      {
        name: 'HR Metrics Catalog',
        summary: '130+ reference metrics across headcount, attrition, recruiting, compensation, and planning.',
        href: '/semantic-hr#metrics',
      },
      {
        name: 'Workforce Use Cases',
        summary: 'From current state through plan sensitivity, RIF scenarios, and forecast modeling.',
        href: '/semantic-hr#use-cases',
      },
      {
        name: 'Architecture & Governance',
        summary: 'Snowflake-native semantic layer, Workday and SuccessFactors parity, and enterprise security.',
        href: '/semantic-hr#architecture',
      },
      {
        name: 'Semantic Modeling & Domain Memory',
        summary: 'Enterprise semantic layers, certified metrics, and analytics engineering for agent-ready context.',
        href: '/platform-engineering#modernization',
      },
    ],
    'ai-analytics': [
      {
        name: 'AI-Powered Analytics & Intelligence',
        summary:
          'Domain analyst agents, governed MCP, and chat-operated warehouse updates in your Snowflake account, plus Cortex and closed-loop monitoring.',
        href: '/platform-engineering#ai',
      },
    ],
    'platform-engineering': [
      {
        name: 'Data Platform & Engineering',
        summary: 'End-to-end data engineering, Streamlit experiences, and BI-to-semantic-layer modernization.',
        href: '/platform-engineering',
      },
    ],
  };
  return map[categoryId] || [];
}

/**
 * Flat offering list for grids and footers: mirrors legacy surfaces with new summaries.
 */
export const OFFERINGS = [
  {
    name: 'Semantic HR',
    categoryId: 'conversational-semantic',
    summary:
      'Out-of-the-box semantic layer for leading HRIS platforms, Semantic HR Copilot + Builder on governed HR metrics.',
    href: '/semantic-hr',
  },
  {
    name: 'Interactive Analytics & BI Catalog',
    categoryId: 'operational-bi',
    summary:
      'Searchable dashboard marketplaces, embedded Power BI, Tableau, and Amazon QuickSight migration, and conversational analytics on governed data.',
    href: '/interactive-analytics',
  },
  {
    name: 'Workforce & Operational Intelligence',
    categoryId: 'operational-bi',
    summary:
      'Operational command centers, KPI ecosystems, flags, triggers, and decision-intelligence agents.',
    href: '/platform-engineering#hris',
  },
  {
    name: 'Snowflake Platforms & Migration (AIM)',
    categoryId: 'migrations-aim',
    summary:
      'Snowflake, Teradata, SQL Server, dbt, AWS, and Azure migration with AI-assisted delivery and governed pipelines.',
    href: '/platform-engineering#snowflake',
  },
  {
    name: 'Embedded BI & Dashboard Marketplace',
    categoryId: 'operational-bi',
    summary:
      'Legacy BI turned into a searchable, embedded dashboard marketplace with interactive analytics.',
    href: '/interactive-analytics',
  },
  {
    name: 'Semantic Modeling & Domain Memory',
    categoryId: 'conversational-semantic',
    summary:
      'Enterprise semantic layers, certified metrics, and analytics engineering that give agents lasting context.',
    href: '/platform-engineering#modernization',
  },
];

export const MESSAGING_PILLARS = [
  {
    title: 'Semantic intelligence on your stack',
    desc: 'Snowflake-centered foundations, domain memory, and governed metrics, so agents and copilots answer with enterprise context, not guesses.',
  },
  {
    title: 'Build on platforms you already trust',
    desc: 'Snowflake, AWS, Azure, Power BI, and Microsoft AI, platform-native products deployed in your account, not multi-tenant SaaS subscriptions.',
  },
  {
    title: 'One AI-ready analytics ecosystem',
    desc: 'Specialist domain agents, orchestration, and governed MCP on Snowflake-centered foundations, so operators can ask and apply approved writes in chat, without a mega-chatbot or multi-tenant agent SaaS. That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.',
  },
  {
    title: 'From idea to product, faster',
    desc: 'Cortex builders, interactive analytics, and semantic layers shorten the path from question to governed dashboard or agent workflow.',
  },
];

/**
 * Conceptual quotes: index-referenced (e.g. ProductExamples). Append only; do not reorder.
 */
export const MESSAGING_QUOTES = [
  {
    text: 'What once took a large program can start with a clear blueprint and the right platform.',
    context: 'Cortex-generated builders',
  },
  {
    text: 'We turn your legacy BI into a searchable, governed dashboard marketplace.',
    context: 'BI catalog and dashboard intelligence',
  },
  {
    text: 'Operational alerts, flags, and statistical monitoring keep every key measure under watch.',
    context: 'Decision intelligence agents',
  },
  {
    text: 'Conversational analytics on governed data, accelerate decisions and analysis.',
    context: 'Conversational analytics',
  },
  {
    text: 'Build on Snowflake, AWS, Azure, Power BI, and platforms you already trust.',
    context: 'Platform partners',
  },
  {
    text: 'Domain memory for analytics agents, governed context that lasts beyond a single dashboard.',
    context: 'Domain memory & semantic layers',
  },
  {
    text: 'Build the AI-ready analytics ecosystem your enterprise already has the foundation for.',
    context: 'Enterprise AI positioning',
  },
  {
    text: 'Close the context gap, from pipelines to managing domain memory.',
    context: 'Semantic foundation',
  },
  {
    text:
      'Modernize BI, migrate workloads, and stand up semantic layers so agents inherit governed context, not another silo.',
    context: 'Migration & modernization',
  },
  {
    text:
      'Scalable enterprise intelligence is not one agent that knows everything. It is specialist analysts on governed semantic views, orchestrated in your account, and usable by your agents or others via MCP without breaking trust.',
    context: 'Multi-agent architecture',
  },
  {
    text:
      'The chat is the operating surface. Operators ask questions, apply approved writes, and maintain reference facts through MCP tools, without a SQL worksheet.',
    context: 'Chat-operated warehouse updates',
  },
];

/** Solution page hero copy: aligned to methodology pillars. */
export const SOLUTION_PAGE_HEROES = {
  dataEngineering: {
    eyebrow: MIGRATION_SCOPE.eyebrow,
    title: MIGRATION_SCOPE.title,
    body: MIGRATION_SCOPE.body,
    quote: MIGRATION_SCOPE.quote,
    primaryCta: { label: 'Scope a migration', href: '/contact?service=snowflake-migration' },
    secondaryCta: { label: 'Explore capabilities', href: '#snowflake' },
  },
  powerBi: {
    eyebrow: 'Interactive analytics · Conversational BI',
    title: 'Interactive Analytics & Conversational BI for modern decision surfaces.',
    body:
      'We build talk-to-your-data experiences, searchable dashboard marketplaces, embedded Power BI, Tableau, and QuickSight, ' +
      'AI-assisted analysis, KPI monitoring and alerts, and interactive decision surfaces on governed semantic context.',
    quote: 'We turn your legacy BI into a searchable dashboard marketplace.',
    primaryCta: { label: 'Scope a dashboard system', href: '/contact' },
    secondaryCta: { label: 'View capabilities', href: '#services' },
  },
  semanticHr: {
    eyebrow: 'Conversational analytics & semantic layers',
    productOverviewTitle: 'One place for trustworthy workforce answers',
    productOverviewBody:
      'Semantic HR ships a Snowflake-native product layer for Workday and SuccessFactors, certified metrics, ' +
      'governed views, and a split-screen analytics experience. Ask in plain language, the Cortex copilot answers ' +
      'with dynamic visualizations in chat, activates the right embedded BI bookmark with filters applied, and ' +
      'lets you follow threads into attrition, hiring, plan vs actual, and growth scenarios without starting over ' +
      'in a new report.',
    methodology:
      'Build on platforms you trust · modernize legacy BI · semantic layers · interactive and conversational ' +
      'analytics · AI-ready decisions · close the context gap.',
  },
};

/** Canonical contact route for Semantic HR page CTAs. */
export const SEMANTIC_HR_CONTACT_HREF = '/contact?service=semantic-hr-demo';

/**
 * Exact homepage body links so product and company URLs are crawlable
 * without opening a nav dropdown.
 */
export const HOME_CRAWL_LINKS = [
  { label: 'Semantic HR', href: '/semantic-hr' },
  { label: 'Interactive Analytics', href: '/interactive-analytics' },
  { label: 'Platform Engineering', href: '/platform-engineering' },
  { label: 'Company', href: '/company' },
];

/** Short FAQ on /semantic-hr. First Ossie mention uses the incubating name. */
export const SEMANTIC_HR_FAQ = {
  eyebrow: 'Questions',
  title: 'Conversational workforce analytics in your account',
  items: [
    {
      question: 'What is Semantic HR?',
      answer:
        'Semantic HR is the MyOrgLab AI workforce product: conversational analytics on certified metrics for Workday and SuccessFactors. Leaders ask in plain language and get governed answers with in-thread visualizations and an embedded BI gallery. It is built in the customer Snowflake account. It is not a staffing service and not a multi-tenant SaaS application.',
    },
    {
      question: 'Where does the analytics run?',
      answer:
        'In the customer Snowflake account. Semantic models, Cortex Intelligence agents, and governed views stay under that customer identity, roles, and policies. MyOrgLab deploys the product there. Workforce data is not hosted in a shared MyOrgLab tenant.',
    },
    {
      question: 'How is conversational analytics different from a dashboard library?',
      answer:
        'Each answer uses the same certified metric definitions as the embedded reports. Follow-up questions stay in one session, from current headcount through trends, plan versus actual, and scenarios, instead of hunting for another report or pasting workforce data into an ungoverned chat tool.',
    },
    {
      question: 'How does Apache Ossie (Incubating) relate to Semantic HR?',
      leadLink: { href: 'https://ossie.apache.org/', label: 'Ossie' },
      answer:
        ' is the vendor-neutral specification for exchanging semantic metadata across analytics, AI, and BI tools. It was formerly known as Open Semantic Interchange. Metrics, dimensions, and relationships keep a portable definition so agents and dashboards can share one meaning. Semantic HR is the workforce product we deploy in the customer account; we align that semantic foundation with Ossie. Ossie is not a MyOrgLab product.',
      sourceLink: { href: 'https://github.com/apache/ossie', label: 'apache/ossie' },
    },
  ],
};

export function semanticHrFaqPlainText(item) {
  if (!item.leadLink) return item.answer;
  const source = item.sourceLink ? ` Source: ${item.sourceLink.label}.` : '';
  return `${item.leadLink.label}${item.answer}${source}`;
}

/** Semantic HR top-nav dropdown — same shape as SOLUTION_CATEGORIES entries. */
export const SEMANTIC_HR_NAV_CATEGORY = {
  id: 'semantic-hr',
  name: 'Semantic HR',
  featured: true,
  description:
    'Snowflake-native workforce intelligence for Workday and SuccessFactors, split-screen copilot, embedded BI gallery, ' +
    'and governed HR metrics in one session.',
  shortDescription:
    'Snowflake-native workforce intelligence: copilot, embedded BI, and governed HR metrics.',
  href: '/semantic-hr',
  icon: 'CircleStackIcon',
  iconClass: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
  offerings: [
    { name: 'HR Metrics Catalog', href: '/semantic-hr#metrics' },
    { name: 'Workforce Use Cases', href: '/semantic-hr#use-cases' },
    { name: 'Architecture & Governance', href: '/semantic-hr#architecture' },
    { name: 'Request Demo', href: SEMANTIC_HR_CONTACT_HREF },
  ],
};

/** Semantic HR solution page: section copy grouped for narrative flow. */
export const SEMANTIC_HR_PAGE = {
  problemEyebrow: 'Why a semantic layer',
  problemTitle: 'Reduce hypotheses. Reach answers faster.',
  problemIntro:
    'Workforce meetings run on assumptions, understaffed engineering, manager-driven attrition, APAC behind plan. ' +
    'Semantic HR collapses assumption-to-evidence in one governed session instead of report hunting and analyst queues.',
  hrisEyebrow: 'Workday & SuccessFactors',
  hrisTitle: 'One semantic layer for both HRIS platforms',
  hrisIntro:
    'Parallel warehouse models and certified metrics for Workday REST and SuccessFactors OData, same definitions in ' +
    'dashboards, reporting views, and copilot answers so you are not rebuilding semantics per system.',
  analyticsEyebrow: 'Snowflake-native experience',
  analyticsTitle: 'Split-screen copilot, BI gallery, and on-demand visualizations',
  analyticsIntro:
    'Semantic HR ships as a React analytics application backed by Snowflake Cortex Intelligence agents, a copilot chat ' +
    'column for on-demand analytic responses with in-chat dynamic visualizations, paired with a right column that ' +
    'embeds your governed BI report gallery (Power BI, Tableau, or another platform) and optional query-native charts ' +
    'with edit capability beneath the embed. Streamlit reference apps and CoWork configuration remain available for ' +
    'Snowflake-native pilots and enterprise rollout patterns.',
  dynamicNarrative:
    'Instead of opening another Power BI report, resetting filters, and waiting days for a one-off pull, leaders iterate ' +
    'through current state, trends, plan comparisons, sensitivity scenarios, and RIF assessments in a single copilot session, ' +
    'each answer backed by certified metrics and paired with the right visualization.',
  dynamicStats: [
    { value: '1 session', label: 'Current state through RIF scenarios' },
    { value: '0 exports', label: 'No Excel handoffs for follow-ups' },
    { value: 'Same metrics', label: 'Certified definitions every turn' },
  ],
  hrisEntityMap: [
    {
      platform: 'Workday',
      source: 'REST API warehouse',
      entities: 'Workers, organizations, jobs, positions, recruiting, compensation, time & absence',
    },
    {
      platform: 'SuccessFactors',
      source: 'OData warehouse',
      entities: 'Employee Central, org structure, job classification, positions, recruiting, compensation',
    },
  ],
  dynamicBenefits: [
    {
      title: 'End report hunting',
      description:
        'No more scrolling through dozens of HR Power BI reports, opening the wrong dashboard, or asking "which report has this number?" The copilot routes each question to the right governed view.',
    },
    {
      title: 'Filters applied for you',
      description:
        'Country, department, job level, period, the entities in your question become report filters automatically. When the Snowflake result supports it, a live chart appears beneath the embed from the same rows.',
    },
    {
      title: 'Pressure-test workforce plans',
      description:
        'Augment budget and forecast baselines with governed actuals, then run sensitivity on attrition, hiring freezes, or headcount targets, labor cost and FTE impact in the same session.',
    },
    {
      title: 'Expand as you learn',
      description:
        'Every new HR domain, metric, or executive dashboard you add to the platform becomes immediately available to the copilot; the intelligence layer grows with your analytics maturity, not against it.',
    },
  ],
};

/** Semantic HR: conversation demo section (six-turn narrative). */
export const SEMANTIC_HR_CONVERSATION = {
  eyebrow: 'Conversation in action',
  title: 'From a simple question to a workforce plan',
  intro:
    'Real executive conversations do not stop at one metric. Semantic HR supports progressive inquiry, start with current ' +
    'state, follow threads across attrition and hiring, augment workforce plans with sensitivity analysis, and model RIF or ' +
    'restructuring scenarios, all in one governed session.',
  semanticEyebrow: 'Governed semantic layer',
  semanticTitle: 'Certified metrics before conversational reach',
  semanticIntro:
    'Every turn maps to the same HR metric definitions your BI team certifies, dimensions, measures, and reporting views ' +
    'in Snowflake, not ad-hoc logic inside the chat surface. The copilot activates pre-approved Power BI bookmarks and ' +
    'applies filters from context; Cortex runs read-only SQL against governed views.',
  semanticPoints: [
    'One semantic model connects natural language to REPORTING and REPORTING_SECURE views',
    'Row access policies and domain roles enforce scope before Power BI or Streamlit render an answer',
    'Each turn keeps its own dashboard memory, follow-ups inherit filters without manual slicer work',
  ],
  demoEyebrow: 'Six-turn demo',
  demoTitle: 'One session, progressive complexity',
  demoIntro:
    'From a baseline headcount question through attrition, hiring, plan variance, forecast, and executive sensitivity, ' +
    'each turn builds on the last without exporting to spreadsheets or opening a new report.',
  footerTitle: 'Your analytics program, your scope',
  footerBody:
    'The examples above illustrate a planning conversation, not a fixed product limit. Your semantic model, governed metrics, ' +
    'and Power BI experience library expand as your organization grows, add new HR domains, custom measures, report bookmarks, ' +
    'and filter dimensions whenever your workforce analytics needs evolve.',
  turns: [
    {
      turn: 1,
      label: 'Current state',
      complexity: 'Simple',
      question: 'What is our current active headcount by department?',
      response:
        'Instant governed headcount breakdown, the headcount dashboard opens pre-filtered to active employees, by department, using the same definitions your BI team certifies.',
      insight: 'Establish the baseline before any deeper analysis.',
    },
    {
      turn: 2,
      label: 'Trend & movement',
      complexity: 'Focused',
      question: 'Which departments had the highest voluntary attrition over the last 12 months?',
      response:
        'Attrition view activates with voluntary-attrition filters applied. Leaders see rates and counts side by side with the plain-language summary, no manual slicer hunting.',
      insight: 'Move from snapshot to movement without leaving the conversation.',
    },
    {
      turn: 3,
      label: 'Multi-thread follow-up',
      complexity: 'Connected',
      question:
        'For those high-attrition departments, how many open requisitions do we have, and what is our average time-to-fill?',
      response:
        'The copilot pivots to recruiting context, open reqs, pipeline stages, and time-to-fill for the departments identified in the prior answer. Each turn keeps its own dashboard memory.',
      insight: 'Follow the thread across domains, attrition leads naturally into hiring capacity.',
    },
    {
      turn: 4,
      label: 'Workforce planning',
      complexity: 'Plan vs actual',
      question:
        'Compare planned headcount to actual by business unit this quarter, where are we over or under plan, and what is the labor cost variance?',
      response:
        'Workforce planning and compensation views combine, plan vs actual headcount by BU, with labor cost against plan surfaced for FP&A and HR leadership alignment.',
      insight: 'Bridge operational HR data with financial workforce planning in one session.',
    },
    {
      turn: 5,
      label: 'Growth & forecast',
      complexity: 'Strategic',
      question:
        'If we hold current hiring velocity and attrition rates, what does engineering headcount look like in six months, and what is the projected labor cost impact compared to plan?',
      response:
        'A multi-metric synthesis, current movement trends, open pipeline, historical hire and separation rates, and plan comparison, each leg answered with governed data and the matching filtered visualization.',
      insight: 'Test growth scenarios and forecast assumptions in minutes, not weeks of analyst cycles.',
    },
    {
      turn: 6,
      label: 'Sensitivity & RIF',
      complexity: 'Executive',
      question:
        'If we hold attrition at current rates, freeze backfills in two business units, and target an 8% headcount reduction in G&A, what is the six-month headcount and labor cost impact vs plan?',
      response:
        'Workforce planning views combine actual, budget, and scenario versions with org and compensation context. The copilot surfaces headcount, FTE, and labor cost deltas by BU, with the planning dashboard and Snowflake chart filtered to the scenario scope.',
      insight: 'Augment the workforce plan in the room, sensitivity and RIF assessments without a separate spreadsheet cycle.',
    },
  ],
};

/** One-line cross-link from Semantic HR trust/governance to Interactive Analytics RBAC band. */
export const SEMANTIC_HR_RBAC_CROSS_LINK = {
  eyebrow: 'Workforce data scope',
  title: 'The same RBAC and row filters HR leaders expect, enforced in Snowflake, not re-built in Power BI.',
  body:
    'Semantic HR inherits manager hierarchy, company scope, and domain roles from the warehouse semantic layer. ' +
    'Compensation, PII, and performance stay separated by role whether the answer surfaces in Streamlit, embedded Power BI, or Cortex.',
  href: '/interactive-analytics#warehouse-governance',
  linkLabel: 'RBAC through the analytics pipeline',
};

/** Enterprise consulting engagement models: no fixed prices or SaaS tiers. */
export const PRICING_HERO = {
  eyebrow: 'Enterprise engagement tiers',
  title: 'Enterprise pricing aligned to semantic and AI outcomes.',
  subtitle:
    'Choose the engagement tier that matches your stage, Semantic Readiness, Intelligence Buildout, or Enterprise AI Platform. ' +
    'Each engagement is scoped to your architecture, governance model, and delivery goals, from Snowflake migration and semantic layers through ' +
    'Cortex Search document intelligence, Java and React application frameworks, and unified platform modernization.',
  primaryCta: { label: 'Request consultation', href: '/contact?service=pricing-enterprise-engagement' },
  secondaryCta: { label: 'Explore solutions', href: '/platform-engineering' },
};

export const PRICING_ENGAGEMENTS = [
  {
    id: 'assessment',
    name: 'Semantic Readiness',
    badge: 'Tier 1',
    bestFit:
      'Teams evaluating migration paths, semantic layer gaps, or BI modernization priorities before committing build budget.',
    engagementType: 'Fixed-scope assessment with executive readout',
    included: [
      'Current-state analytics and platform inventory',
      'Migration and modernization roadmap (Snowflake, Teradata, SQL Server, dbt, AWS, Azure)',
      'Semantic layer and BI-to-data-layer gap analysis',
      'Prioritized initiative backlog with effort bands',
    ],
    deliverables: [
      'Architecture and migration strategy document',
      'Semantic and interactive analytics recommendations',
      '90-day execution plan with owner mapping',
    ],
    relatedSolutions: ['Modernized Migrations', 'Semantic Intelligence', 'Interactive Analytics'],
    cta: 'Start assessment',
    href: '/contact?service=pricing-enterprise-engagement',
    accent: 'migration',
  },
  {
    id: 'sprint',
    name: 'Intelligence Buildout',
    badge: 'Tier 2',
    bestFit:
      'Organizations ready to ship a bounded outcome, a semantic layer slice, dashboard marketplace, AIM migration wave, or copilot pilot.',
    engagementType: 'Time-boxed implementation sprint (4–12 weeks)',
    included: [
      'Dedicated builder squad with weekly checkpoints',
      'Governed semantic models or embedded Power BI deliverable',
      'AIM-assisted migration or BI-to-data-layer uplift where in scope',
      'Documentation, handoff, and hypercare window',
    ],
    deliverables: [
      'Production-ready semantic layer, dashboards, or migration tranche',
      'Runbook and operational checklist',
      'Stakeholder enablement session',
    ],
    relatedSolutions: ['Interactive Analytics', 'Semantic HR', 'Platform Engineering'],
    cta: 'Scope an engagement',
    href: '/contact?service=pricing-enterprise-engagement',
    accent: 'brand',
    featured: true,
  },
  {
    id: 'partnership',
    name: 'Enterprise AI Platform',
    badge: 'Tier 3',
    bestFit:
      'Enterprises consolidating migrations, semantic foundations, interactive BI, and AI agents under one operating model.',
    engagementType: 'Multi-quarter build partnership with phased releases',
    included: [
      'Program leadership across migration, semantic, and BI workstreams',
      'Snowflake-centered platform engineering on AWS/Azure',
      'Searchable dashboard marketplaces and conversational analytics',
      'Decision agents, flags, triggers, and measure monitoring',
    ],
    deliverables: [
      'Integrated AI-ready analytics ecosystem on your stack',
      'Certified metric catalog and semantic governance model',
      'Copilot and agent workflows on governed data',
    ],
    relatedSolutions: ['All solution categories'],
    cta: 'Request consultation',
    href: '/contact?service=pricing-enterprise-engagement',
    accent: 'enterprise',
  },
  {
    id: 'managed',
    name: 'Managed Intelligence Program',
    badge: 'Operate & evolve',
    bestFit:
      'Teams that want ongoing platform stewardship, semantic layer evolution, PBI ops intelligence, and agent tuning.',
    engagementType: 'Retained partnership with SLA-backed cadence',
    included: [
      'Semantic layer and metric catalog stewardship',
      'Power BI ops monitoring, refresh, and marketplace curation',
      'Agent and copilot tuning on Snowflake Cortex',
      'Quarterly modernization and migration backlog reviews',
    ],
    deliverables: [
      'Monthly health and adoption reporting',
      'Continuous improvement backlog with shipped increments',
      'Executive briefing on platform and AI readiness',
    ],
    relatedSolutions: ['AI & Decision Agents', 'Interactive Analytics', 'Platform Engineering'],
    cta: 'Scope an engagement',
    href: '/contact?service=pricing-enterprise-engagement',
    accent: 'semantic',
  },
];

export const PRICING_INCLUDED = [
  'Dedicated analytics architect for your engagement',
  'Structured discovery aligned to your solution category',
  'Documented data model and architecture artifacts',
  'Staged delivery with review checkpoints',
  'Git-managed codebase, yours to own',
  'Post-launch hypercare support period',
];

/**
 * Contact / enterprise consultation flow: source of truth.
 *
 * `CONTACT_SERVICES` is the canonical service inquiry list + slug mapping. Each
 * entry's `value` doubles as the `?service=<slug>` query-param slug used for
 * CTA prefill routing across the site.
 */
export const CONTACT_HERO = {
  eyebrow: 'Enterprise consultation',
  title: 'Start with a Semantic Readiness Conversation.',
  body:
    'Tell us about your architecture, BI landscape, and enterprise AI goals. A senior architect aligns scope to ' +
    'semantic layer assessment, Snowflake migration, legacy BI modernization, Power BI, Tableau, and QuickSight modernization, conversational analytics, and decision intelligence delivery.',
  platforms:
    'dbt · SQL Server · Teradata · AWS · Azure · Snowflake · Power BI · Tableau · Streamlit · QuickSight',
};

export const CONTACT_SERVICES = [
  {
    value: 'semantic-layer-assessment',
    label: 'Semantic Layer Assessment',
    subject: 'Semantic layer assessment',
    messagePlaceholder:
      "I'm interested in assessing our semantic layer readiness, metric governance, and enterprise AI context model.",
    accent: 'semantic',
  },
  {
    value: 'snowflake-migration',
    label: 'Snowflake Migration',
    subject: 'Snowflake migration',
    messagePlaceholder:
      "I'm interested in migrating legacy warehouses or reporting workloads into Snowflake enterprise architecture.",
    accent: 'migration',
  },
  {
    value: 'legacy-bi-modernization',
    label: 'Legacy BI Modernization',
    subject: 'Legacy BI modernization',
    messagePlaceholder:
      "I'm interested in modernizing legacy BI into a searchable, governed analytics marketplace.",
    accent: 'brand',
  },
  {
    value: 'conversational-bi',
    label: 'Conversational BI',
    subject: 'Conversational BI and talk-to-data',
    messagePlaceholder:
      "I'm interested in conversational BI experiences that let teams talk to governed enterprise data.",
    accent: 'semantic',
  },
  {
    value: 'dashboard-marketplace',
    label: 'Dashboard Marketplace',
    subject: 'Dashboard marketplace buildout',
    messagePlaceholder:
      "I'm interested in building a searchable dashboard marketplace with embedded analytics and governed discovery.",
    accent: 'brand',
  },
  {
    value: 'semantic-hr-demo',
    label: 'Semantic HR Demo',
    subject: 'Semantic HR demo request',
    messagePlaceholder:
      "I'm interested in a Semantic HR demonstration for Snowflake-native workforce intelligence.",
    accent: 'semantic',
  },
  {
    value: 'enterprise-ai-application-build',
    label: 'Enterprise AI Application Build',
    subject: 'Enterprise AI application build',
    messagePlaceholder:
      "I'm interested in building an enterprise AI analytics application with semantic context and governed data.",
    accent: 'enterprise',
  },
  {
    value: 'decision-intelligence-agents',
    label: 'Decision Intelligence Agents',
    subject: 'Decision intelligence agents',
    messagePlaceholder:
      "I'm interested in governed MCP agents, chat-operated warehouse updates, KPI monitoring, and next-best-action agents.",
    accent: 'enterprise',
  },
  {
    value: 'platform-engineering',
    label: 'Platform Engineering',
    subject: 'Platform engineering and semantic architecture',
    messagePlaceholder:
      "I'm interested in semantic platform engineering across Snowflake architecture, data layers, and BI consumption surfaces.",
    accent: 'migration',
  },
  {
    value: 'unified-snowflake-platform',
    label: 'Unified Snowflake Platform Strategy',
    subject: 'Unified Snowflake platform strategy',
    messagePlaceholder:
      "I'm interested in unifying transactional systems, analytical warehouses, BI tools, and document repositories into a Snowflake-centered intelligence platform.",
    accent: 'migration',
  },
  {
    value: 'cortex-search-document-intelligence',
    label: 'Cortex Search / Document Intelligence',
    subject: 'Cortex Search and document intelligence',
    messagePlaceholder:
      "I'm interested in making PDFs, policies, contracts, handbooks, and enterprise documents searchable and usable by AI agents through Cortex Search.",
    accent: 'semantic',
  },
  {
    value: 'java-react-enterprise-apps',
    label: 'Java / React Enterprise AI Applications',
    subject: 'Java and React enterprise AI application build',
    messagePlaceholder:
      "I'm interested in building Java backend services and React interfaces that connect securely to Snowflake, Postgres, Cortex, and governed enterprise data.",
    accent: 'enterprise',
  },
  {
    value: 'pricing-enterprise-engagement',
    label: 'Pricing / Enterprise Engagement',
    subject: 'Pricing & enterprise engagement',
    messagePlaceholder:
      "I'd like to understand your engagement models and scope a pricing conversation for an enterprise " +
      'analytics partnership.',
    accent: 'enterprise',
  },
  {
    value: 'general-inquiry',
    label: 'General Inquiry',
    subject: '',
    messagePlaceholder:
      "Tell us about your initiative, current platforms, and the outcomes you're aiming for.",
    accent: 'brand',
  },
];

/** Form-facing option list, `value`, `label`, `subject`, `messagePlaceholder`. */
export const CONTACT_SERVICE_OPTIONS = CONTACT_SERVICES;

/** Default service when no slug is supplied or the slug is unknown. */
export const DEFAULT_CONTACT_SERVICE = 'general-inquiry';

/** Tolerant slug aliases, keep older/legacy CTA links working. */
const CONTACT_SERVICE_ALIASES = {
  general: 'general-inquiry',
  'pricing-enterprise': 'pricing-enterprise-engagement',
  pricing: 'pricing-enterprise-engagement',
  snowflake: 'snowflake-migration',
  migrations: 'snowflake-migration',
  'power-bi': 'dashboard-marketplace',
  'interactive-analytics': 'dashboard-marketplace',
  'semantic-intelligence': 'semantic-layer-assessment',
  'conversational-analytics': 'conversational-bi',
  'ai-decision-agents': 'decision-intelligence-agents',
  'modernized-migrations': 'snowflake-migration',
  'snowflake-data-platform': 'snowflake-migration',
  'interactive-analytics-power-bi': 'dashboard-marketplace',
  'delivery-model': 'pricing-enterprise-engagement',
};

/** Resolve a `?service=<slug>` (or select value) to a contact service option. */
export function getContactServiceBySlug(slug) {
  if (!slug) return null;
  const normalized = CONTACT_SERVICE_ALIASES[slug] || slug;
  return CONTACT_SERVICES.find((s) => s.value === normalized) || null;
}

/**
 * Canonical service value for form prefills and generate_lead.
 * Tries each candidate (form value, then `?service=`) through slug aliases.
 * Unknown slugs are returned as-is so the event still carries the query value.
 */
export function resolveContactServiceValue(...candidates) {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const option = getContactServiceBySlug(candidate);
    if (option) return option.value;
  }
  return candidates.find(Boolean) || '';
}

/**
 * Build a contact href for a service slug. General / unknown → bare `/contact`.
 * Use everywhere CTAs route into the consultation flow.
 */
export function contactHref(slug) {
  const option = getContactServiceBySlug(slug);
  if (!option || option.value === DEFAULT_CONTACT_SERVICE) return '/contact';
  return `/contact?service=${option.value}`;
}

/** Alias kept for components already importing `getContactUrl`. */
export const getContactUrl = contactHref;

/** Footer solution links derived from categories + flagship. */
export function getFooterSolutionLinks() {
  const links = [];
  NAV_SOLUTIONS.forEach((cat) => {
    links.push({ name: cat.name, href: cat.href });
  });
  const hr = OFFERINGS.find((o) => o.href === '/semantic-hr');
  if (hr && !links.some((l) => l.href === hr.href)) {
    links.unshift({ name: hr.name, href: hr.href, featured: true });
  }
  return links;
}

/* ────────────────────────────────────────────────────────────────────────
 * Power BI / Interactive Analytics page, copy source of truth.
 * Appended exports consumed by solutions/pbi-development/**.
 * Positioning, Power BI as part of a broader enterprise intelligence layer
 * connecting dashboards, metadata, semantic models, Snowflake, Streamlit,
 * and Cortex Analyst, not generic dashboard development.
 * ──────────────────────────────────────────────────────────────────────── */

/** Single contact route for every CTA on the Power BI page. */
export const PBI_CONTACT_HREF = '/contact?service=dashboard-marketplace';

/** Core page copy, hero through outcomes. */
export const PBI_PAGE = {
  hero: {
    eyebrow: 'Conversational analytics · Semantic layers · Governed BI',
    title: 'Interactive Analytics & Conversational BI on Governed Semantic Context',
    body:
      'We build talk-to-data experiences that connect Snowflake, Cortex Analyst, Streamlit applications, embedded BI, ' +
      'and certified semantic models into one governed analytics experience. Your teams can ask questions, generate charts, ' +
      'explore dashboards, and follow up in natural language, all grounded in the same metric catalog, business glossary, ' +
      'and trusted definitions used across Power BI, Tableau, QuickSight, and enterprise reporting.',
    quote:
      'Define metrics once in the semantic layer. Every AI answer, chart, dashboard, and follow-up inherits the same governed meaning.',
    primaryCta: { label: 'Request consultation', href: PBI_CONTACT_HREF },
    secondaryCta: { label: 'View capabilities', href: '#semantic-discovery' },
    platforms: [
      'Snowflake & Cortex Analyst',
      'Certified Semantic Models',
      'Streamlit Applications',
      'Embedded BI',
      'Metric Catalog',
      'Power BI · Tableau · QuickSight',
    ],
    manifesto: {
      eyebrow: 'Talk to your data',
      body:
        'Governed semantic models let teams move beyond static dashboards and report queues. Business users can ask questions, ' +
        'receive narrative answers, generate visualizations, and drill into trusted data without needing to know table structures, ' +
        'SQL logic, or where the right dashboard lives.',
    },
  },

  problem: {
    id: 'dashboard-sprawl',
    eyebrow: 'The problem',
    title: 'Dashboard sprawl is quietly eroding trust in your analytics.',
    body:
      'Years of self-service BI leave enterprises with hundreds of duplicated, disconnected, and undocumented ' +
      'dashboards. Nobody can find the certified version, metrics drift between reports, and governance becomes ' +
      'guesswork. The result is slow decisions and shrinking confidence in the numbers.',
    bullets: [
      'Duplicated reports with conflicting metric definitions',
      'Disconnected workspaces with no shared catalog or lineage',
      'Hard-to-find dashboards, discovery by tribal knowledge',
      'Poorly governed access, ownership, and certification',
    ],
    visual: 'sprawl',
    accent: 'neutral',
  },

  biGallery: {
    id: 'bi-gallery',
    eyebrow: 'BI gallery · Dashboard marketplace',
    title: 'A searchable, governed gallery for every BI asset.',
    body:
      'Legacy BI becomes a searchable dashboard marketplace, we catalog your Power BI and Tableau estate, with Amazon QuickSight ' +
      'where AWS analytics fit, organizing reports, metrics, filters, and semantic models with business context. ' +
      'Teams search by question or KPI and land on the certified asset instead of rebuilding it.',
    bullets: [
      'Searchable catalog of dashboards, reports, pages, and visuals',
      'Extracted measures, metrics, filters, and semantic model metadata',
      'Business context, ownership, and certification on every asset',
      'Sprawl reduction through reuse, deduplication, and curation',
    ],
    visual: 'gallery',
    accent: 'semantic',
  },

  embeddedSemantic: {
    id: 'embedded-semantic',
    eyebrow: 'Power BI Embedded · Semantic discovery',
    title: 'Embedded Power BI that surfaces the right insight, not just the right report.',
    body:
      'Power BI Embedded plus semantic discovery surfaces the pages, visuals, filters, and metrics for the question ' +
      'at hand, a semantic signal layer that helps teams build on platforms they trust. The experience shifts from ' +
      '“where is the dashboard?” to “what insight do I need?” as intelligence moves toward the Snowflake data layer.',
    bullets: [
      'Power BI Embedded experiences inside your own applications',
      'Report components, pages, filters, and measures as semantic signals',
      'Context-aware discovery of the most relevant visuals and metrics',
      'Static reporting reframed as an interactive decision layer',
    ],
    visual: 'embedded',
    accent: 'semantic',
  },

  talkToData: {
    id: 'talk-to-data',
    eyebrow: 'Talk to your data',
    title: 'CoWork agents that answer with charts, tables, and narrative insight.',
    body:
      'Snowflake CoWork brings conversational analytics to teams already standardizing on Snowflake enterprise agents. ' +
      'Each question routes through governed semantic models before charts, key observations, and follow-ups appear in the workspace.',
  },

  conversationalSurfaces: {
    id: 'conversational-surfaces',
    eyebrow: 'CoWork · Streamlit · Cortex',
    title: 'One semantic layer, multiple conversational surfaces.',
    body:
      'The semantic model registry is shared, CoWork agents, Streamlit reference copilots, and Cortex Intelligence all consume ' +
      'the same certified metrics, dimensions, and business glossary. Surfaces differ; semantics do not.',
    bullets: [
      'CoWork for enterprise agent rollout with key observations and follow-ups',
      'Streamlit reference patterns for custom Snowflake-native copilot UX',
      'Cortex Analyst and Search wired to the same metric catalog and document indexes',
      'Define metrics once, consume in chat, agents, and embedded BI',
    ],
    visual: 'integration',
    accent: 'semantic',
  },

  outcomes: {
    eyebrow: 'Outcomes',
    title: 'What a governed semantic layer unlocks for conversational analytics.',
    items: [
      {
        title: 'Certified metrics in every answer',
        body: 'CoWork and Cortex agents query semantic models, not ad-hoc spreadsheet logic, so headcount, attrition, and cost mean the same thing everywhere.',
      },
      {
        title: 'Faster workforce answers',
        body: 'Charts, tables, and narrative insight in one agent turn, without report hunting or manual slicer resets.',
      },
      {
        title: 'Semantic layer portability',
        body: 'Metric definitions, dimensions, and glossary terms travel from Snowflake to CoWork, Streamlit, and embedded BI.',
      },
      {
        title: 'Executive-ready narrative',
        body: 'Key observations and suggested follow-ups turn governed query output into briefings operators use live.',
      },
      {
        title: 'Governance by design',
        body: 'Row access policies and masking enforced in CORE before any conversational surface sees the data.',
      },
      {
        title: 'Path to the full product',
        body: 'CoWork pairs with Semantic HR for split-screen copilot, BI gallery, and dynamic visualizations on the same semantic foundation.',
      },
    ],
  },
};

/** Semantic HR page, BI gallery & report fatigue (media-first; HR-specific copy). */
export const SEMANTIC_HR_BI_EXPERIENCE = {
  sprawl: {
    id: 'dashboard-sprawl',
    eyebrow: 'Report fatigue',
    title: 'HR leaders should not hunt Power BI workspaces to answer one workforce question.',
    body:
      'Disparate HR dashboards, conflicting headcount definitions, and bookmark guesswork slow every planning cycle. ' +
      'Semantic HR replaces report hunting with governed answers in one split-screen session.',
    bullets: [
      'Stop opening the wrong dashboard or resetting slicers for every follow-up',
      'Certified HR metrics stay consistent across copilot chat and embedded Power BI',
    ],
    accent: 'neutral',
  },
  gallery: {
    id: 'bi-gallery',
    eyebrow: 'In-app BI gallery',
    title: 'Every governed HR report, one click from the conversation.',
    body:
      'The gallery column surfaces certified Power BI bookmarks, headcount, attrition, hiring, compensation, and planning, ' +
      'synced to the question at hand instead of tribal workspace search.',
    bullets: [
      'Curated HR report library with ownership and certification context',
      'Agentic bookmark selection applies filters from conversational intent',
    ],
    accent: 'semantic',
  },
  embedded: {
    id: 'embedded-semantic',
    eyebrow: 'Embedded Power BI · Dynamic viz',
    title: 'Governed dashboards plus on-demand charts from the same query.',
    body:
      'Agentic Power BI opens the certified view with filters applied from context. Where the Snowflake result supports it, ' +
      'a dynamic visualization renders beneath the embed from the exact rows returned, curated report plus immediate chart.',
    bullets: [
      'Power BI Embedded inside the React analytics application',
      'Editable dynamic visualizations generated from governed SQL output',
      'Split-screen keeps chat, gallery, and charts in one workforce session',
    ],
    accent: 'semantic',
  },
};

/** Interactive Analytics, in-depth discovery (Close the Context Gap style). */
export const INTERACTIVE_ANALYTICS_DISCOVERY = {
  eyebrow: 'In-depth discovery',
  title: 'Discover and understand metrics, dashboards, and domain knowledge',
  lead: 'Enterprises do not lack data. They lack shared understanding of what the data means.',
  body:
    'Before conversational analytics can be trusted, teams need a governed map of certified metrics, dashboard lineage, ' +
    'business rules, and domain expertise. We inventory BI estates, extract semantic signals from reports, and build ' +
    'enterprise memory so every surface, CoWork, Cortex, and Power BI, speaks the same language.',
  badges: ['Certified metrics', 'Dashboard catalog', 'Domain knowledge', 'Business glossary'],
  flowLabel: 'Fragmented inputs → Semantic understanding → Trusted conversation',
  columns: [
    {
      title: 'Discover',
      titleClass: 'text-ink-500',
      bg: 'bg-white',
      ring: 'ring-ink-200',
      badgeClass: 'badge-neutral',
      items: ['Dashboards', 'Metrics', 'Filters', 'Documents', 'SME knowledge'],
    },
    {
      title: 'Understand',
      titleClass: 'text-brand-900',
      bg: 'bg-[rgba(0,18,63,0.06)]',
      ring: 'ring-[rgba(0,18,63,0.14)]',
      badgeClass: 'badge-semantic',
      items: ['Semantic models', 'Metric catalog', 'Lineage', 'Glossary', 'Domain memory'],
    },
    {
      title: 'Converse',
      titleClass: 'text-brand-700',
      bg: 'bg-brand-50/70',
      ring: 'ring-brand-100',
      badgeClass: 'badge-brand',
      items: ['CoWork agents', 'Cortex chat', 'Key observations', 'Follow-ups', 'Trends'],
    },
  ],
};

/** Interactive Analytics, semantic foundation & context gap. */
export const INTERACTIVE_ANALYTICS_CONTEXT_GAP = {
  eyebrow: 'Semantic foundation',
  title: 'Close the context gap before you scale conversational analytics',
  body:
    'Metrics live in dashboards, business rules live in pipelines, definitions live in documents, and expertise lives with people. ' +
    'A semantic foundation unifies that context so CoWork, Streamlit, and Cortex agents do not invent meaning turn by turn.',
  points: [
    {
      title: 'Semantic layer as system of record',
      body: 'Certified metrics, dimensions, joins, and verified queries, versioned like production code, not tribal spreadsheet logic.',
    },
    {
      title: 'Enterprise memory for AI',
      body: 'Business glossary, document indexes, and domain context wired into Cortex Search alongside structured semantics.',
    },
    {
      title: 'One foundation, many surfaces',
      body: 'CoWork agents, Streamlit copilots, and Cortex Intelligence consume the same semantic models, surfaces differ, definitions do not.',
    },
  ],
};

/** Compact Apache Ossie advocate callout — home, company, and IA semantic foundation. */
export const APACHE_OSSIE_HUB = {
  eyebrow: 'Apache Ossie (Incubating)',
  body:
    'Apache Ossie (Incubating) is the vendor-neutral open specification for exchanging semantic models (metrics, dimensions, relationships, AI context) across analytics, BI, and agents, formerly Open Semantic Interchange (OSI). MyOrgLab aligns to the Apache Incubator specification so governed business meaning can move across agents, BI platforms, and enterprise tools. Ossie is not a MyOrgLab product.',
  primaryCta: {
    label: 'Explore Apache Ossie',
    href: 'https://github.com/apache/ossie',
  },
  docsCta: {
    label: 'Ossie at Apache',
    href: 'https://ossie.apache.org/',
  },
  secondaryCta: {
    label: 'Build an Ossie-aligned semantic layer',
    href: '/contact',
  },
};

/** Interactive Analytics, governance across BI and chat (not database infrastructure). */
export const INTERACTIVE_ANALYTICS_GOVERNANCE = {
  eyebrow: 'Governed access across surfaces',
  title: 'From Power BI governed performance to agentic chat with the same RLS',
  body:
    'Analytics governance is not only warehouse infrastructure, it is consistent RBAC, row access policies, and dynamic masking ' +
    'from direct query through Power BI embedded performance to agentic and conversational chat tools. Users see the same scope ' +
    'whether they open a report, run SQL, or ask CoWork a question.',
  bullets: [
    'RBAC and row access policies (RAP) enforced on semantic reporting views, not reimplemented per chat turn',
    'Power BI service accounts and embedded experiences inherit governed scope and performance guardrails',
    'Direct query and Cortex paths validated against certified semantic models before execution',
    'Agentic Power BI bookmark selection and conversational chat share the same governed RLS context',
    'Domain roles separate compensation, PII, performance, and EEO, masking follows the user, not the tool',
  ],
};

/** Interactive Analytics, conversational analytics & intelligent agents. */
export const INTERACTIVE_ANALYTICS_CONVERSATIONAL_AGENTS = {
  eyebrow: 'Conversational analytics · Intelligent agents',
  title: 'CoWork agents and decision intelligence on governed semantic context',
  body:
    'We build talk-to-data experiences where Snowflake CoWork agents, Cortex Intelligence, and Streamlit copilots consume the same ' +
    'certified metrics and business glossary as your Power BI estate. Each turn returns charts, tables, key observations, and ' +
    'suggested follow-ups, plus decision intelligence agents that monitor KPIs, detect anomalies, and recommend next actions.',
  points: [
    {
      title: 'Governed answers in plain language',
      body: 'Questions route through semantic models before SQL runs, headcount, attrition, and cost mean the same thing in chat as in BI.',
    },
    {
      title: 'CoWork · Streamlit · Cortex Intelligence',
      body: 'Multiple conversational surfaces share one semantic registry; surfaces differ, definitions do not.',
    },
    {
      title: 'Decision intelligence agents',
      body: 'Agents inherit domain memory to monitor measures, trigger alerts, and surface narrative insight, not isolated chat prompts.',
    },
    {
      title: 'Analysts closer to the business',
      body: 'Less time rebuilding reports; more time on scenario modeling, predictive analytics, and designing AI-powered decision systems.',
    },
  ],
};

/** Interactive Analytics, command hub integrated into existing BI. */
export const INTERACTIVE_ANALYTICS_COMMAND_HUB = {
  eyebrow: 'Integrated command hub',
  title: 'The transparency a centralized analytics tool should provide',
  body:
    'A governed command hub gives leaders the visibility traditional BI estates rarely deliver in one place, query history, trending ' +
    'questions, certified metric usage, and governed answers wired into Power BI, your metric catalog, and Snowflake semantic models. ' +
    'Embedded BI and conversational analytics stay synchronized instead of living in disconnected silos.',
  points: [
    {
      title: 'Central visibility operators can trust',
      body: 'See what users ask, which metrics drive follow-ups, and where trending questions signal training or governance gaps.',
    },
    {
      title: 'Full domain context in every session',
      body: 'Chat inherits certified metrics, active filters, bookmark memory, and business glossary terms, not isolated prompts.',
    },
    {
      title: 'Integrated with existing BI systems',
      body: 'Power BI embeds, dashboard gallery, and conversational column share one semantic foundation and one access model.',
    },
    {
      title: 'Command hub, not sidecar chat',
      body: 'Query, visualize, narrate, and act from a single governed surface, the transparency layer your analytics program needs.',
    },
  ],
};

/** @deprecated Use INTERACTIVE_ANALYTICS_CONTEXT_GAP, kept for import compatibility. */
export const INTERACTIVE_ANALYTICS_SEMANTIC = INTERACTIVE_ANALYTICS_CONTEXT_GAP;

/** Power BI page, agentic architecture steps (paired with conversation demo section). */
export const PBI_AGENTIC_ARCHITECTURE = {
  id: 'agentic-architecture',
  eyebrow: 'Agentic architecture',
  title: 'Analytics that move at the speed of conversation',
  body:
    'Interactive analytics connects certified metrics, Power BI bookmarks, and Snowflake Cortex so each question ' +
    'activates the right governed view with filters set from context.',
  steps: [
    {
      step: '01',
      title: 'Start with the business question',
      description: 'Leaders ask in plain language; the hub maps intent to certified metrics and report context.',
    },
    {
      step: '02',
      title: 'Cortex interprets on governed data',
      description: 'Snowflake Cortex routes to semantic models and read-only SQL validated before execution.',
    },
    {
      step: '03',
      title: 'Semantic layer responds',
      description: 'Dimensions, measures, and reporting views stay consistent across BI and conversational surfaces.',
    },
    {
      step: '04',
      title: 'Embedded Power BI activates',
      description: 'Agentic bookmark selection applies filters; supplementary charts render from the same query rows.',
    },
    {
      step: '05',
      title: 'Follow the thread',
      description: 'Each turn keeps dashboard memory so follow-ups inherit scope without manual slicer work.',
    },
    {
      step: '06',
      title: 'Evidence in the meeting',
      description: 'Assumptions collapse into governed answers fast enough to use live with executives and operators.',
    },
  ],
  differentiatorTitle: 'Key differentiator',
  differentiatorBody:
    'The value is governed, dynamic analytics without report fatigue, approved Power BI visuals plus inference-based ' +
    'charts from certified SQL output, not disconnected chat answers.',
};

/** Power BI page, multi-turn conversational demo. */
export const PBI_CONVERSATION_DEMO = {
  id: 'conversation-demo',
  eyebrow: 'Conversation demo',
  title: 'From discovery to operational action in one session',
  intro:
    'A progressive inquiry across dashboards, metrics, and Snowflake-backed answers, each turn building on the last ' +
    'inside your BI gallery and Streamlit hub.',
  turns: [
    {
      turn: 1,
      label: 'Discovery',
      question: 'Which certified dashboard answers active pipeline by region?',
      response: 'The gallery surfaces the governed sales pipeline report with region filters pre-applied.',
    },
    {
      turn: 2,
      label: 'Metric drill',
      question: 'How did conversion rate trend versus last quarter for those regions?',
      response: 'Cortex returns certified conversion metrics; the embedded visual opens with the same scope.',
    },
    {
      turn: 3,
      label: 'Cross-domain',
      question: 'For underperforming regions, what is open inventory and days of supply?',
      response: 'The hub pivots to operational views while preserving filters from the prior answer.',
    },
    {
      turn: 4,
      label: 'Plan variance',
      question: 'Compare forecast to actual revenue for this quarter by business unit.',
      response: 'Planning and finance bookmarks combine with governed Snowflake measures in one thread.',
    },
    {
      turn: 5,
      label: 'Scenario',
      question: 'If we hold current close rates, what does pipeline coverage look like in ninety days?',
      response: 'Scenario logic runs on certified assumptions with the matching filtered visualization.',
    },
    {
      turn: 6,
      label: 'Action',
      question: 'Flag any BU where coverage drops below threshold and notify owners.',
      response: 'Threshold monitoring ties BI assets to alerts and workflow actions on Snowflake intelligence.',
    },
  ],
  footerTitle: 'Your catalog grows with your program',
  footerBody:
    'Add dashboards, measures, Streamlit actions, and Cortex reach as your interactive analytics practice matures.',
};

/**
 * Interactive Analytics, RBAC and row-level enforcement through the analytics pipeline.
 * Aligned with semantichr-synthetic-data, Snowflake RBAC, DDM, row access policies,
 * REPORTING / REPORTING_SECURE, Power BI service accounts, Streamlit session roles.
 */
export const INTERACTIVE_ANALYTICS_RBAC = {
  id: 'warehouse-governance',
  eyebrow: 'RBAC & row-level enforcement',
  title: 'Access control that travels with the data, source to every analytics surface.',
  body:
    'Enterprise analytics fail when permissions live only in the BI tool. We enforce RBAC, dynamic data masking, ' +
    'and row access policies in Snowflake CORE first, propagated through RAW → STAGING → CORE ETL, then expose ' +
    'governed REPORTING views and role-scoped REPORTING_SECURE views to Power BI service accounts, Streamlit copilots, ' +
    'and Cortex with the same worker, company, and manager-hierarchy scope at every layer.',
  bullets: [
    'Snowflake RBAC and schema grants, BI_READER, domain roles (HR_COMP, HR_PII, HR_PERFORMANCE), CORTEX_USER',
    'Dynamic data masking on PII, compensation, performance, and EEO columns, enforced through secure views',
    'Row access policies on company scope and manager hierarchy, not reimplemented per dashboard',
    'Power BI Embedded via service account; Streamlit session role switches for sensitive Cortex domains',
    'Defense in depth, grants, masking, and row filtering must all fire, schema access alone is insufficient',
  ],
  scopeExamples: [
    { role: 'Manager', scope: 'Own record plus direct and indirect reports via hierarchy cache' },
    { role: 'HRBP', scope: 'Company or region rows authorized in HR_USER_COMPANY_SCOPE' },
    { role: 'Executive BI', scope: 'REPORTING views only, masking applied, no REPORTING_SECURE' },
  ],
  visual: 'rbac',
  accent: 'semantic',
};

/** Dashboard lifecycle → BI intelligence lifecycle (8-step, preserved + enhanced). */
export const PBI_LIFECYCLE = {
  eyebrow: 'BI intelligence lifecycle',
  title: 'From dashboard lifecycle to BI intelligence lifecycle.',
  body:
    'Traditional dashboard development ends when the report is published. Interactive analytics advocates extend ' +
    'the lifecycle, catalog BI assets, embed into Streamlit and Snowflake-native hubs, add talk-to-data and Cortex ' +
    'Analyst reach, and layer flags, triggers, and statistical monitoring from the BI layer to the data layer.',
  steps: [
    {
      title: 'Inventory fragmented dashboards',
      body: 'Discover and inventory the full Power BI estate across workspaces, owners, and disconnected reports.',
    },
    {
      title: 'Catalog assets & metadata',
      body: 'Catalog reports, pages, visuals, metrics, filters, and semantic models with their business context.',
    },
    {
      title: 'Identify duplication & gaps',
      body: 'Surface duplication, coverage gaps, and governance issues, conflicting metrics and uncertified assets.',
    },
    {
      title: 'Build a searchable BI gallery',
      body: 'Stand up a searchable BI gallery and dashboard marketplace so teams find certified assets fast.',
    },
    {
      title: 'Integrate into Streamlit & Snowflake',
      body: 'Embed BI into Streamlit and Snowflake-native interfaces connected to the governed data layer.',
    },
    {
      title: 'Connect to Cortex Analyst',
      body: 'Wire metadata and business logic into Cortex Analyst and conversational analytics for plain-language reach.',
    },
    {
      title: 'Add operational intelligence',
      body: 'Layer in monitoring, alerts, flags, and triggers so analytics drive action, not just observation.',
    },
    {
      title: 'Improve via governance & iteration',
      body: 'Continuously improve through governance, certification, and iteration as the estate and needs evolve.',
    },
  ],
};

/** "Ready to modernize your BI ecosystem?" closing CTA (preserved + enhanced). */
export const PBI_READY_TO_BUILD = {
  eyebrow: 'Ready to build',
  title: 'Ready to modernize your BI ecosystem?',
  subtitle:
    'Turn disconnected dashboards into a searchable marketplace, Streamlit operational hubs, and conversational ' +
    'analytics on Snowflake, Power BI Embedded, Tableau, Cortex Analyst, QuickSight where AWS fits, and decision agents.',
  ctas: [
    { label: 'Scope a BI Modernization Engagement', href: PBI_CONTACT_HREF, variant: 'primary' },
    { label: 'Build a BI Gallery', href: PBI_CONTACT_HREF, variant: 'secondary' },
    { label: 'Modernize Power BI Operations', href: PBI_CONTACT_HREF, variant: 'secondary' },
  ],
  pillars: [
    {
      title: 'Searchable BI gallery',
      body: 'A governed dashboard marketplace for every report, page, metric, and semantic model.',
    },
    {
      title: 'Streamlit & conversational',
      body: 'Streamlit hubs plus talk-to-data, Power BI Embedded, and Cortex Analyst on governed Snowflake data.',
    },
    {
      title: 'Operationally aware',
      body: 'Monitoring, alerts, and decision workflows connecting BI to Snowflake and action.',
    },
  ],
};

/** Local review stubs for uncommitted delivery WIP. Do not include in the P0 PR. */
export const CONVERSATIONAL_INDUSTRY_FIT = {
  label: 'Industry patterns',
  industries: [
    'HR & people analytics',
    'Legal research & case intelligence',
    'Trucking & logistics operations',
    'Finance & operations',
  ],
};

export const DELIVERY_HERO = {
  eyebrow: 'How we deliver',
  title: 'Working results early, and every few weeks after.',
  subtitle:
    'We build AI and decision support in your own cloud account, shipping small tested increments your team reviews every sprint.',
  primaryCta: { label: 'Talk delivery with us', href: '/contact?service=delivery-model' },
  secondaryCta: { label: 'See engagement models', href: '/pricing' },
};

export const DELIVERY_ACCOUNT = {
  eyebrow: 'In the customer account',
  title: 'Built on governed data',
  body:
    'AI and decision support built in your own cloud account on governed data. We work on the data platform and cloud you already run, whether that is Azure, AWS or another provider, with Power BI or your BI tool when interactive analytics is in scope. This is not a multi-tenant SaaS bolt-on.',
};

export const DELIVERY_VISION = {
  title: 'Trusted partner delivery',
  body:
    'Be a trusted partner to the business by delivering integrated and intelligent data, analytics, and AI services with operational rigor.',
};

export const DELIVERY_STRATEGIES = [
  'Leverage AI and automation wherever they improve quality, speed, or reliability',
  'Enhance enterprise initiatives already underway; do not reinvent the stack for its own sake',
  'Become stewards of process excellence across data, semantics, BI, and AI delivery',
  'Invest in our work, structure, and people so capability compounds across engagements',
];

export const DELIVERY_PRINCIPLES_INTRO = {
  eyebrow: 'Our principles',
  title: 'Three commitments behind every engagement',
  intro: 'These shape how we plan, build and hand over work in your account.',
};

export const DELIVERY_GUIDING_PRINCIPLES = [
  'Value early and often: usable results every sprint',
  'Collaboration over rigid contracts: you review each sprint',
  'Every change ships with its own unit tests, run in CI',
];

/** Definition of Done section header, restored word for word from c0bd0d0 (Michael 2026-10-25: visuals only). */
export const DELIVERY_DOD_HEADER = Object.freeze({
  eyebrow: 'Entry & exit criteria',
  intro:
    'Every user story moves through nine defined states with clear owners and exit criteria, from capture to warranty close.',
  labels: Object.freeze({ state: 'State', owner: 'Owner', criteria: 'Exit criteria' }),
});

/** Screen-reader description for the DoD card lanes (Lilian spec, household-tracker PR #55). */
export const DELIVERY_DOD_DESC =
  'Nine states from Backlog to Closed, grouped into seven lifecycle bands. Business stakeholders give formal UAT sign-off at state 6.';

/** Pre-#26 Definition of Done content, restored verbatim from c0bd0d0. */
export const DELIVERY_DEFINITION_OF_DONE = [
  { state: 'Backlog', owner: 'Product owner / team', exit: 'Clear initial request captured.' },
  { state: 'In Grooming', owner: 'Agile team', exit: 'Acceptance criteria written; estimate agreed.' },
  { state: 'Prioritized', owner: 'Product owner', exit: 'Committed to the upcoming sprint backlog.' },
  { state: 'Development', owner: 'Developers', exit: 'Development complete; unit testing executed.' },
  { state: 'QA', owner: 'QA engineers', exit: 'Verified in test against all acceptance criteria.' },
  { state: 'UAT', owner: 'Business stakeholders', exit: 'Formal UAT sign-off provided.' },
  { state: 'Pre-Production', owner: 'Release manager / Dev lead', exit: 'Release artifact frozen.' },
  { state: 'Released', owner: 'Ops lead / Dev lead', exit: 'Deployed to production; smoke tested.' },
  { state: 'Closed', owner: 'Product owner', exit: 'Warranty complete; archived.' },
];

export const DELIVERY_LIFECYCLE_BANDS = [
  { band: 'Discovery', states: ['Backlog'] },
  { band: 'Planning', states: ['In Grooming', 'Prioritized'] },
  { band: 'Build', states: ['Development'] },
  { band: 'Verification', states: ['QA', 'UAT'] },
  { band: 'Release gate', states: ['Pre-Production'] },
  { band: 'Live', states: ['Released'] },
  { band: 'Archive', states: ['Closed'] },
];

export const DELIVERY_AGILE_LOOP = [
  { step: 'Capture the request', body: 'You tell us what you need. We write it down in plain terms.' },
  { step: 'Agree and schedule', body: 'We agree on acceptance criteria and an estimate, then commit it to a sprint.' },
  { step: 'Build and test', body: 'We build it in your account. Every change ships with its own unit tests in CI.' },
  { step: 'You review', body: 'Your team checks it against the agreed criteria and signs off.' },
  { step: 'Release and support', body: 'We deploy, smoke test, and support it through the warranty period.' },
];

export const DELIVERY_AGILE_OUTCOME =
  'Smaller releases, faster feedback, clearer progress, and continuous alignment with business value.';

/** Compact DeliveryBand copy for /, /company and solution pages (Richard, PR18). */
export const DELIVERY_BAND_COMPACT = {
  heading: 'How we deliver',
  principles: [
    'Working results early, then every few weeks, built in your own account.',
    'Acceptance criteria agreed with your business, and every change ships with its own tests.',
  ],
  link: { label: 'See how we deliver', href: '/delivery' },
};

export const DELIVERY_SPRINT_FLOW = {
  eyebrow: 'Sprint to release',
  title: 'How one request becomes delivered work',
  note: 'Work moves through each stage in order; each is owned and checked. Highlighted card: Being built.',
  boardHeading: 'Example board: request to support complete',
  ghostTag: 'Example card',
  ariaLabel: 'Example board showing nine delivery stages, scrolls horizontally',
  srSummary:
    'Example board with nine stages: Requested, Agreed, Scheduled, Being built, Tested by us, Your review, Ready to release, Live, Support complete. The card marked In progress is in Being built.',
  steps: [
    { title: 'Backlog', body: 'Requested: work is logged and ordered by value.' },
    { title: 'Sprint', body: 'Agreed and scheduled, then being built with its own tests.' },
    { title: 'Pipeline', body: 'Tested by us in CI, then your review before release.' },
    { title: 'Release', body: 'Ready to release, live and smoke tested, then support complete.' },
  ],
  sample: {
    column: 'Development',
    label: 'In progress',
    text: 'As a finance analyst, I want a trusted customer view with key attributes, so I can answer revenue questions without rebuilding the data each month.',
    detail: 'Done when: unit tests pass in CI and the view matches the agreed criteria.',
  },
  board: [
    { state: 'Backlog', label: '1. Requested', ghost: 'New request logged' },
    { state: 'In Grooming', label: '2. Agreed', ghost: 'Acceptance criteria agreed' },
    { state: 'Prioritized', label: '3. Scheduled', ghost: 'Picked for next sprint' },
    { state: 'Development', label: '4. Being built' },
    { state: 'QA', label: '5. Tested by us', ghost: 'Checked against criteria' },
    { state: 'UAT', label: '6. Your review', ghost: 'Your team signs off' },
    { state: 'Pre-Production', label: '7. Ready to release', ghost: 'Release build locked' },
    { state: 'Released', label: '8. Live', ghost: 'Deployed and smoke tested' },
    { state: 'Closed', label: '9. Support complete', ghost: 'Support window complete' },
  ],
};

export const DELIVERY_TRADEMARK =
  'Azure DevOps is a trademark of Microsoft Corporation. MyOrgLab is not affiliated with or endorsed by Microsoft.';

export const DELIVERY_VALUE_GROWTH = [
  { label: 'Iteration 1', body: 'Deliver the first working increment' },
  { label: 'Iteration 2', body: 'Add feedback-driven improvements' },
  { label: 'Iteration N', body: 'Expand release scope over time' },
];

export const DELIVERY_SYSTEM_OF_RECORD = {
  title: 'Azure DevOps as the system of record',
  lead:
    'Azure DevOps is our preferred single system of record for planning, tracking, and delivering work, used across every role on the engagement. It leads the backlog, sprints, boards, pipelines, and releases.',
  pillars: [
    {
      title: 'Backlog',
      body: 'Goals, features and user stories stay structured, visible and traceable. The product owner commits prioritized work to the upcoming sprint backlog.',
    },
    {
      title: 'Boards',
      body: 'The main board is a Kanban view of every state from Backlog through Closed. The sprints board is the timebox view of Development and QA.',
    },
    {
      title: 'Sprints',
      body: 'A sprint confirms scope, acceptance criteria, and ownership, then builds, tests, reviews the increment, and refines the next backlog.',
    },
  ],
  boards: [
    { name: 'Main board (Kanban)', body: 'All nine states from Backlog through Closed.' },
    { name: 'Sprints board', body: 'Sprint timebox zoom into Development and QA.' },
  ],
  pipelines: {
    title: 'Pipelines and releases',
    body:
      'Azure DevOps pipelines lead continuous integration and continuous delivery for the increment: build, test, and the artifact a release promotes. Pre-Production freezes that artifact. Released means it is deployed to production and smoke tested. Closed means warranty is complete and the item is archived.',
  },
  supporting: {
    title: 'The delivery toolchain',
    body:
      'Azure DevOps or GitHub holds the backlog and runs CI/CD. dbt builds tested analytics models, Airflow orchestrates pipelines, and Terraform keeps infrastructure in code. Tool choice follows what your organization already runs.',
  },
};
